// Configuration for form field layout
// Determines which fields should be displayed as half-width (side by side)
// and groups fields into logical sections

export type FieldWidth = "full" | "half" | "third";

// Fields that should be half-width (displayed side by side)
export const halfWidthFields: string[] = [
  // Personal info - names
  "first_name",
  "second_name",
  "last_name",
  "maternal_last_name",
  "paternal_last_name",
  // Contact
  "email",
  "phone",
  "phone_number",
  "cellphone",
  // Dates
  "birth_date",
  "birthdate",
  "start_date",
  "end_date",
  // Demographics
  "gender",
  "marital_status",
  "civil_status",
  "marital_regime",
  // Location
  "country",
  "birth_country",
  "nationality",
  "state",
  "birth_state",
  // Address fields
  "street",
  "exterior_number",
  "interior_number",
  "postal_code",
  "neighborhood",
  "municipality",
  "city",
  // Company address
  "company_street",
  "company_exterior_number",
  "company_interior_number",
  "company_postal_code",
  "company_neighborhood",
  "company_municipality",
  // Job info
  "occupation",
  "company_name",
  "job_title",
  "job_seniority",
  "company_turn",
  // Financial
  "monthly_income",
  "rent_amount",
  "bank_name",
  "clabe",
  "deposit_amount",
  "maintenance_amount",
  // Property / Contract
  "property_usage",
  "property_use",
  "intended_use",
  "total_rent_period",
  "account_holder",
  // References
  "job_reference_name",
  "job_reference_phone",
  "job_reference_email",
  "job_reference_relationship",
  "personal_reference_name",
  "personal_reference_phone",
  "personal_reference_email",
  "personal_reference_relationship",
  // Previous landlord
  "last_landlord_name",
  "last_landlord_phone",
  "last_landlord_email",
  "last_landlord_relationship",
  // New fields for expanded layout
  "monthly_expenses",
  "company_website",
  "rfc_company",
  "creation_date",
  "business_sector",
  "notary",
  "current_landlord",
  "monthly_sales_expenses",
  "monthly_admin_expenses",
  "monthly_production_expenses",
  "public_instrument",
  "name_1",
  "name_2",
  "house_type",
  "rfc",
  "curp",
  "includes_furniture",
  "current_landlord_phone",
];

// Fields that should be third-width (very short numeric fields, displayed 3 per row)
export const thirdWidthFields: string[] = [
  "people_in_house",
  "people_depend",
  "employee_number",
  "office_number",
  "property_occupants",
  "payment_day",
  "grace_days",
  "days_grace",
  "grace_period",
];

// Get the width for a specific field
export const getFieldWidth = (fieldKey: string): FieldWidth => {
  // Check third-width FIRST (before half) to avoid substring collision
  const isThirdWidth = thirdWidthFields.some(
    (pattern) => fieldKey.includes(pattern) || fieldKey === pattern
  );
  if (isThirdWidth) return "third";

  const isHalfWidth = halfWidthFields.some(
    (pattern) => fieldKey.includes(pattern) || fieldKey === pattern
  );
  return isHalfWidth ? "half" : "full";
};

// Field section groupings for visual organization
export const fieldSections: Record<string, string[]> = {
  "Datos personales": [
    "first_name",
    "second_name",
    "last_name",
    "paternal_last_name",
    "maternal_last_name",
    "birth_date",
    "birthdate",
    "gender",
    "birth_country",
    "birth_state",
    "nationality",
    "marital_status",
    "civil_status",
    "marital_regime",
  ],
  "Contacto": ["email", "phone", "phone_number", "cellphone"],
  "Dirección": [
    "street",
    "exterior_number",
    "interior_number",
    "postal_code",
    "neighborhood",
    "municipality",
    "city",
    "state",
    "country",
  ],
  "Información laboral": [
    "occupation",
    "company_name",
    "job_title",
    "job_seniority",
    "company_turn",
    "company_website",
    "monthly_income",
  ],
  "Dirección de trabajo": [
    "company_street",
    "company_exterior_number",
    "company_interior_number",
    "company_postal_code",
    "company_neighborhood",
    "company_municipality",
  ],
};

// Get section name for a field
export const getFieldSection = (fieldKey: string): string | null => {
  for (const [section, fields] of Object.entries(fieldSections)) {
    if (fields.some((f) => fieldKey.includes(f) || fieldKey === f)) {
      return section;
    }
  }
  return null;
};
